import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Target, Database, Cloud, Shield, Zap, Users, Brain, Layers, ArrowLeft, Clock, CheckCircle, Calendar, Download, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import { services } from '@/data/mock';
import usePageTitle from '@/hooks/usePageTitle';
import ServiceFAQ from '@/components/services/ServiceFAQ';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  usePageTitle(service ? service.title : "Service Details", service ? `${service.shortDescription} ElitePlus IT consulting services in UAE.` : "ElitePlus IT consulting service details in UAE.");

  const iconMap = {
    Target,
    Database,
    Cloud,
    Shield,
    Zap,
    Users,
    Brain,
    Layers
  };

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-500">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[service.icon] || Target;
  const otherServices = services.filter(s => s.slug !== slug).slice(0, 3);

  const getCTAButton = () => {
    switch (service.slug) {
      case 'it-strategy':
        return { text: 'Book a Strategy Call', icon: Calendar };
      case 'erp-advisory':
        return { text: 'Download ERP Discovery Checklist', icon: Download };
      case 'cloud-modernization':
        return { text: 'Book a Cloud Readiness Call', icon: Calendar };
      case 'cybersecurity':
        return { text: 'Get Your Security Scorecard', icon: Shield };
      case 'digital-transformation':
        return { text: 'Talk to a Transformation Advisor', icon: Calendar };
      default:
        return { text: 'Book a Consultation', icon: Calendar };
    }
  };

  const cta = getCTAButton();
  const CTAIcon = cta.icon;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={`${service.title} - IT consulting services UAE`}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-500 transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>

          <div className="max-w-3xl">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/25">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              {service.fullDescription}
            </p>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-4">
              {service.timeline && (
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg">
                  <Clock className="w-5 h-5 text-cyan-500" />
                  <span className="text-slate-700">Timeline: {service.timeline}</span>
                </div>
              )}
              {service.engagement && (
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg">
                  <Target className="w-5 h-5 text-teal-500" />
                  <span className="text-slate-700">{service.engagement}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <article className="lg:col-span-2 space-y-12" itemScope itemType="https://schema.org/Service">
              <meta itemProp="name" content={service.title} />
              <meta itemProp="description" content={service.detailedDescription || service.fullDescription} />
              <meta itemProp="provider" content="ElitePlus Solutions" />
              <meta itemProp="areaServed" content="United Arab Emirates" />

              {/* Detailed Description */}
              {service.detailedDescription && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">Overview</h2>
                  <p className="text-slate-600 leading-relaxed" itemProp="serviceOutput">{service.detailedDescription}</p>
                </div>
              )}

              {/* Vendor Logos */}
              {service.vendorLogos && service.vendorLogos.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">Technology Partners</h2>
                  <div className="flex flex-wrap items-center gap-8">
                    {service.vendorLogos.map((logo) => (
                      <div key={logo.file} className="flex items-center justify-center px-6 py-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-cyan-300 transition-colors duration-300">
                        <img
                          src={`${process.env.PUBLIC_URL}/partners/${logo.file}`}
                          alt={logo.name}
                          loading="lazy"
                          decoding="async"
                          className="max-h-12 sm:max-h-14 w-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Problems We Solve */}
              {service.problems && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">What We Solve</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.problems.map((problem, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-cyan-400/50 transition-colors duration-300"
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center mb-3">
                          <span className="text-red-500 font-bold">{idx + 1}</span>
                        </div>
                        <p className="text-slate-600">{problem}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliverables */}
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">What We Deliver</h2>
                <div className="space-y-3">
                  {service.deliverables.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-cyan-400/50 transition-colors duration-300"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-slate-600 pt-1">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcomes */}
              {service.outcomes && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">Deliverables</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.outcomes.map((outcome, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl"
                      >
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Platforms */}
              {service.platforms && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">Platforms We Support</h2>
                  <div className="flex flex-wrap gap-3">
                    {service.platforms.map((platform, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 hover:border-cyan-400/50 transition-colors duration-300"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Section */}
              <ServiceFAQ faqs={service.faqs} serviceTitle={service.title} />
            </article>

            {/* Right Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* CTA Card */}
                <div className="p-6 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl">
                  <h3 className="text-xl font-bold text-slate-800 mb-4">Ready to Get Started?</h3>
                  <p className="text-slate-500 text-sm mb-6">
                    Let's discuss how we can help your organization with {service.title.toLowerCase()}.
                  </p>
                  <Link to="/contact">
                    <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
                      <CTAIcon className="w-5 h-5 mr-2" />
                      {cta.text}
                    </Button>
                  </Link>
                </div>

                {/* Other Services */}
                <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Other Services</h3>
                  <div className="space-y-3">
                    {otherServices.map((s) => {
                      const OtherIcon = iconMap[s.icon] || Target;
                      return (
                        <Link
                          key={s.id}
                          to={`/services/${s.slug}`}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 group"
                        >
                          <div className="w-10 h-10 rounded-lg bg-slate-200 group-hover:bg-cyan-500/10 flex items-center justify-center transition-colors duration-300">
                            <OtherIcon className="w-5 h-5 text-slate-500 group-hover:text-cyan-500 transition-colors duration-300" />
                          </div>
                          <span className="text-slate-600 group-hover:text-slate-800 transition-colors duration-300 text-sm">
                            {s.title}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServiceDetailPage;
